/**
 * Worked Examples Component
 * Interactive SSP calculation examples showing real-world scenarios
 */

import React, { useState } from 'react';
import {
  BookMarked,
  ChevronLeft,
  ChevronRight,
  Play,
  CheckCircle,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import { formatCurrency } from '../utils/sspCalculations';
import {
  calculateAWE,
  calculateSSPCurrent,
  calculateSSP2026,
} from '../utils/sspCalculations';

const EXAMPLES = [
  {
    id: 'standard-ft',
    title: 'Standard Full-Time Employee',
    description: 'Full-time employee with 5-day work week, earning above LEL, sick for 5 days',
    employee: {
      qualifyingDays: 5,
      typicalShiftLength: 8,
    },
    earnings: [400, 400, 400, 400, 400, 400, 400, 400],
    absence: {
      totalDays: 5,
      rosteredDays: 5,
      isLinkedPeriod: false,
    },
    insights: [
      'AWE well above LEL - eligible under current rules',
      '3 waiting days under current rules reduce payment significantly',
      '2026: All 5 days payable immediately',
    ],
  },
  {
    id: 'pt-below-lel-current',
    title: 'Part-Time Below LEL (Current Rules)',
    description: 'Part-time worker earning below £125/week - shows current exclusion',
    employee: {
      qualifyingDays: 2,
      typicalShiftLength: 6,
    },
    earnings: [100, 100, 100, 100, 100, 100, 100, 100],
    absence: {
      totalDays: 4,
      rosteredDays: 2,
      isLinkedPeriod: false,
    },
    insights: [
      'AWE £100/week is below LEL (£125)',
      'Current rules: Not eligible for any SSP',
      '2026: Receives 80% of AWE = £80/week',
    ],
  },
  {
    id: 'short-absence',
    title: 'Short Absence (1-3 Days)',
    description: '2-day sickness - demonstrates PIW requirement',
    employee: {
      qualifyingDays: 5,
      typicalShiftLength: 8,
    },
    earnings: [250, 250, 250, 250, 250, 250, 250, 250],
    absence: {
      totalDays: 2,
      rosteredDays: 2,
      isLinkedPeriod: false,
    },
    insights: [
      'Only 2 days sick - no PIW formed (need 4+)',
      'Current rules: £0 (PIW not met)',
      '2026: £0 (PIW requirement unchanged)',
      'Note: Waiting day removal does NOT affect short absences',
    ],
  },
  {
    id: 'shift-worker-7qd',
    title: 'Shift Worker with 7 Qualifying Days',
    description: 'Variable shift worker with 7 QDs, only 3 rostered shifts during sickness',
    employee: {
      qualifyingDays: 7,
      typicalShiftLength: 10,
    },
    earnings: [300, 280, 320, 300, 290, 310, 300, 300],
    absence: {
      totalDays: 7,
      rosteredDays: 3,
      isLinkedPeriod: false,
    },
    insights: [
      'Sick for 7 calendar days, but only 3 were rostered shifts',
      'SSP only paid for rostered qualifying days (not all 7)',
      'Daily rate: £118.75 ÷ 7 = £16.96 (current)',
      '2026: No waiting days means all 3 rostered shifts paid',
    ],
  },
  {
    id: 'linked-period',
    title: 'Linked Periods',
    description: 'Second absence within 8 weeks - demonstrates waiting day exemption',
    employee: {
      qualifyingDays: 5,
      typicalShiftLength: 8,
    },
    earnings: [180, 180, 180, 180, 180, 180, 180, 180],
    absence: {
      totalDays: 5,
      rosteredDays: 4,
      isLinkedPeriod: true, // This is the key!
    },
    insights: [
      'This absence is within 8 weeks of a previous PIW',
      'Waiting days NOT served again (already served in first PIW)',
      'Current: All 4 rostered days payable immediately',
      '2026: Same result (waiting days already abolished)',
    ],
  },
  {
    id: 'low-earner-80percent',
    title: 'Low Earner 80% Rate (2026)',
    description: 'Employee earning below £154.06 threshold - shows 80% calculation',
    employee: {
      qualifyingDays: 5,
      typicalShiftLength: 7,
    },
    earnings: [120, 120, 120, 120, 120, 120, 120, 120],
    absence: {
      totalDays: 5,
      rosteredDays: 5,
      isLinkedPeriod: false,
    },
    insights: [
      'AWE £120/week is below £154.06 threshold',
      'Current: Below LEL - not eligible',
      '2026: Receives 80% of AWE = £96/week',
      '2026: Daily rate = £96 ÷ 5 = £19.20/day',
    ],
  },
  {
    id: 'long-term',
    title: 'Long-Term Sickness',
    description: '6-week absence showing cumulative cost',
    employee: {
      qualifyingDays: 5,
      typicalShiftLength: 8,
    },
    earnings: [350, 350, 350, 350, 350, 350, 350, 350],
    absence: {
      totalDays: 42, // 6 weeks
      rosteredDays: 30, // 6 weeks × 5 days
      isLinkedPeriod: false,
    },
    insights: [
      '6 weeks of sickness (42 calendar days)',
      '30 rostered qualifying days across the period',
      'Current: 27 days paid (30 - 3 waiting days)',
      '2026: All 30 rostered days paid',
      'Significant cumulative impact of waiting day removal',
    ],
  },
  {
    id: 'near-lel',
    title: 'Employee Near LEL Threshold',
    description: 'AWE just at the £125 threshold - demonstrates borderline eligibility',
    employee: {
      qualifyingDays: 5,
      typicalShiftLength: 6,
    },
    earnings: [125, 125, 125, 125, 125, 125, 125, 125],
    absence: {
      totalDays: 5,
      rosteredDays: 5,
      isLinkedPeriod: false,
    },
    insights: [
      'AWE exactly £125 (at LEL threshold)',
      'Current: Just eligible - receives full £118.75/week',
      '2026: Receives 80% of AWE = £100/week (below £154.06 threshold)',
      'Counterintuitively gets less in 2026 than current!',
    ],
  },
];

export default function WorkedExamples({ onLoadExample }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const example = EXAMPLES[currentIndex];

  const nextExample = () => {
    setCurrentIndex((prev) => (prev + 1) % EXAMPLES.length);
  };

  const prevExample = () => {
    setCurrentIndex((prev) => (prev - 1 + EXAMPLES.length) % EXAMPLES.length);
  };

  // Calculate results for current example
  const aweResult = calculateAWE(example.earnings);
  const currentSSP = calculateSSPCurrent({
    awe: aweResult.awe,
    qualifyingDays: example.employee.qualifyingDays,
    totalSickDays: example.absence.totalDays,
    rosteredDays: example.absence.rosteredDays,
    isLinkedPeriod: example.absence.isLinkedPeriod,
  });
  const newSSP = calculateSSP2026({
    awe: aweResult.awe,
    qualifyingDays: example.employee.qualifyingDays,
    totalSickDays: example.absence.totalDays,
    rosteredDays: example.absence.rosteredDays,
  });

  const handleLoadExample = () => {
    if (onLoadExample) {
      onLoadExample({
        employee: {
          ...example.employee,
          id: `Example ${currentIndex + 1}`,
          contractedHours: 20,
          avgPickupHours: 0,
          hourlyRate: 11.50,
        },
        earnings: example.earnings,
        absence: example.absence,
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <BookMarked className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Worked Examples</h2>
        </div>
        <p className="text-green-100">
          Real-world scenarios showing how SSP calculations work under current and April 2026 rules
        </p>
      </div>

      {/* Example Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={prevExample}
          className="btn-ghost btn-sm"
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </button>
        <div className="text-center">
          <p className="text-sm text-slate-600">
            Example {currentIndex + 1} of {EXAMPLES.length}
          </p>
          <div className="flex gap-1 mt-1 justify-center">
            {EXAMPLES.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-primary-600' : 'bg-slate-300'
                }`}
              />
            ))}
          </div>
        </div>
        <button
          onClick={nextExample}
          className="btn-ghost btn-sm"
          disabled={currentIndex === EXAMPLES.length - 1}
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Example Card */}
      <div className="card border-2 border-primary-200">
        <div className="card-header bg-primary-50">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold text-primary-900 mb-1">{example.title}</h3>
              <p className="text-sm text-primary-700">{example.description}</p>
            </div>
            {onLoadExample && (
              <button onClick={handleLoadExample} className="btn-primary btn-sm whitespace-nowrap">
                <Play className="w-4 h-4" />
                Try This
              </button>
            )}
          </div>
        </div>

        <div className="card-body space-y-6">
          {/* Scenario Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-medium text-slate-600 uppercase mb-2">Employee</p>
              <div className="space-y-1 text-sm">
                <p>Qualifying Days: <span className="font-semibold">{example.employee.qualifyingDays}</span></p>
                <p>AWE: <span className="font-semibold">{formatCurrency(aweResult.awe)}/week</span></p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-medium text-slate-600 uppercase mb-2">Absence</p>
              <div className="space-y-1 text-sm">
                <p>Total Days: <span className="font-semibold">{example.absence.totalDays}</span></p>
                <p>Rostered Days: <span className="font-semibold">{example.absence.rosteredDays}</span></p>
                <p>Linked: <span className="font-semibold">{example.absence.isLinkedPeriod ? 'Yes' : 'No'}</span></p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-medium text-slate-600 uppercase mb-2">PIW Status</p>
              <div className="flex items-center gap-2">
                {example.absence.totalDays >= 4 ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">PIW Formed</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="text-sm font-semibold text-red-700">No PIW</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Results Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Rules */}
            <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50">
              <h4 className="font-semibold text-primary-900 mb-3">Current Rules (2025/26)</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-primary-700">Eligible:</span>
                  <span className={`font-semibold ${currentSSP.eligible ? 'text-green-700' : 'text-red-700'}`}>
                    {currentSSP.eligible ? 'Yes' : 'No'}
                  </span>
                </div>
                {!currentSSP.eligible && (
                  <div className="bg-red-100 border border-red-200 rounded p-2 text-xs text-red-800">
                    {currentSSP.reason}
                  </div>
                )}
                {currentSSP.eligible && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-primary-700">Daily Rate:</span>
                      <span className="font-mono text-primary-900">{formatCurrency(currentSSP.dailyRate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-primary-700">Waiting Days:</span>
                      <span className="font-mono text-primary-900">{currentSSP.waitingDays}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-primary-700">Days Paid:</span>
                      <span className="font-mono text-primary-900">{currentSSP.sspDaysPaid}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between pt-2 border-t border-primary-300">
                  <span className="font-semibold text-primary-900">Total SSP:</span>
                  <span className="font-mono font-bold text-lg text-primary-900">
                    {formatCurrency(currentSSP.totalSSP)}
                  </span>
                </div>
              </div>
            </div>

            {/* 2026 Rules */}
            <div className="border-2 border-accent-200 rounded-lg p-4 bg-accent-50">
              <h4 className="font-semibold text-accent-900 mb-3">April 2026 Rules</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-accent-700">Eligible:</span>
                  <span className="font-semibold text-green-700">Yes (universal)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-accent-700">Weekly Rate:</span>
                  <span className="font-mono text-accent-900">
                    {formatCurrency(newSSP.weeklyRate)}
                    {newSSP.isReducedRate && <span className="text-xs ml-1">(80%)</span>}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-accent-700">Daily Rate:</span>
                  <span className="font-mono text-accent-900">{formatCurrency(newSSP.dailyRate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-accent-700">Waiting Days:</span>
                  <span className="font-mono text-accent-900">0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-accent-700">Days Paid:</span>
                  <span className="font-mono text-accent-900">{newSSP.sspDaysPaid}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-accent-300">
                  <span className="font-semibold text-accent-900">Total SSP:</span>
                  <span className="font-mono font-bold text-lg text-accent-900">
                    {formatCurrency(newSSP.totalSSP)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Difference */}
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-red-900">Impact of 2026 Changes:</span>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-red-900 font-mono">
                  +{formatCurrency(newSSP.totalSSP - currentSSP.totalSSP)}
                </p>
                {currentSSP.totalSSP > 0 && (
                  <p className="text-sm text-red-700">
                    ({(((newSSP.totalSSP - currentSSP.totalSSP) / currentSSP.totalSSP) * 100).toFixed(0)}% increase)
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Insights */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-semibold text-blue-900 mb-2">💡 Key Insights:</p>
            <ul className="space-y-1">
              {example.insights.map((insight, idx) => (
                <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
